/**
 * RGM Contests · Phase 1 — Judge Workspace.
 *
 * Single-page loop over the judge's visible entries. Playback is
 * routed through the CANONICAL global player (`usePlayer()` from
 * `components/player/PlayerProvider`) — no per-page `<audio>` is
 * built here. Because contest entries carry a canonical
 * `ref_id` (submission_id for tracks, beat_id for beats) rather
 * than a track_audio_url in the ref_snapshot, we hand the real
 * canonical track identity to the player, which lazy-fetches the
 * playback URL and respects `singles_locked` server-side redaction
 * exactly like every other RGM surface.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePlayer } from "@/components/player/PlayerProvider";
import { getCoverUrl, optimizeImage } from "@/lib/mediaUrls";
import ScoringPanel from "@/components/contest/ScoringPanel";

/**
 * Build a canonical player payload from a contest entry.
 *  - Tracks:  trackId = ref_id (canonical submission_id); PlayerProvider
 *             lazy-fetches /track/{id} for the master URL & singles gate.
 *  - Beats:   trackId = "beat_" + beat_id AND kind = "beat" — matches
 *             the BeatDetailPage playback contract exactly.
 * Returns null when the entry has no canonical ref_id (nothing to play).
 */
const _buildPlayerPayload = (entry) => {
  if (!entry || !entry.ref_id) return null;
  const snap = entry.ref_snapshot || {};
  const isBeat = entry.entry_type === "beat";
  // Canonical cover resolution — honour the FULL field priority used
  // by image_url_helpers.canonical_track_cover on the backend:
  //   cover_url → cover_art → cover_image → cover_image_path.
  // The server re-resolves these from the source track/beat on every
  // GET /judge/contests/{cid} (Phase 15.8f), so the snapshot delivered
  // here is already canonical — but we still walk the full chain
  // locally in case a legacy field slips through. Route through
  // `getCoverUrl` so bare filenames surface the branded RGM
  // placeholder instead of a broken image. The judging card and the
  // persistent player both consume this value — they stay in sync.
  const firstCanonical = (
    snap.cover_url || snap.cover_art || snap.cover_image || snap.cover_image_path || null
  );
  const resolvedCover = getCoverUrl({
    cover_url: firstCanonical,
    cover_art: snap.cover_art,
  });
  if (isBeat) {
    return {
      trackId: `beat_${entry.ref_id}`,
      beat_id: entry.ref_id,
      title: snap.title || entry.title || "Untitled beat",
      artist_name: snap.artist_name || "Unknown producer",
      cover_url: resolvedCover,
      kind: "beat",
    };
  }
  return {
    submission_id: entry.ref_id,
    trackId: entry.ref_id,
    track_title: snap.title || entry.title || "Untitled track",
    artist_name: snap.artist_name || "Unknown artist",
    cover_url: resolvedCover,
  };
};

export default function JudgeContestWorkspace() {
  const { contestId } = useParams();
  const navigate = useNavigate();
  const player = usePlayer();
  const [state, setState] = useState(null);
  const [i, setI] = useState(0);
  const [draftScores, setDraftScores] = useState({});
  const [draftNotes, setDraftNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/judge/contests/${contestId}`);
        setState(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load workspace");
        navigate("/judge");
      }
    })();
  }, [contestId, navigate]);

  const entries = useMemo(() => state?.entries || [], [state]);
  const criteria = state?.config?.criteria || [];
  const scale = state?.config?.scale || { min: 1, max: 10 };
  const entry = entries[i];
  const existing = entry && (state?.my_reviews?.[entry.entry_id] || null);
  const locked = !!state?.contest?.judging_closed_at;

  useEffect(() => {
    if (!entry) return;
    setDraftScores(existing?.scores || {});
    setDraftNotes(existing?.notes || "");
  }, [entry, existing?.review_id]); // eslint-disable-line

  const done = useMemo(() => (
    entries.filter((e) => (state?.my_reviews?.[e.entry_id]?.submitted_at)).length
  ), [entries, state]);

  const payload = useMemo(() => _buildPlayerPayload(entry), [entry]);
  const currentPlayerTrackId = player?.track?.trackId || null;
  const isThisPlaying = !!payload && currentPlayerTrackId === payload.trackId && player.isPlaying;

  const handlePlayToggle = () => {
    if (!payload) {
      toast.error("No audio available for this entry.");
      return;
    }
    // Same-track: hand off to the global player's toggle so pause/resume
    // preserves position. Different track: fire play() so it lazy-fetches
    // and starts. sourceLabel identifies these plays as judging so they
    // never accidentally count as Featured plays.
    if (currentPlayerTrackId === payload.trackId) {
      player.toggle();
      return;
    }
    player.play(payload, { sourceLabel: "⚖️ Judge Workspace" });
  };

  const persist = async (submit) => {
    if (!entry) return;
    if (submit) {
      for (const c of criteria) {
        if (draftScores[c.criterion_id] == null) {
          toast.error(`Missing score for ${c.label}`); return;
        }
      }
    }
    setSaving(true);
    try {
      await api.put(`/judge/contests/${contestId}/entries/${entry.entry_id}/review`, {
        scores: draftScores, notes: draftNotes, submit,
      });
      toast.success(submit ? "Review submitted" : "Draft saved");
      const fresh = await api.get(`/judge/contests/${contestId}`);
      setState(fresh.data);
      if (submit) setI(Math.min(i + 1, entries.length - 1));
    } catch (e) {
      const d = e?.response?.data?.detail || "Could not save";
      toast.error(String(d));
    } finally { setSaving(false); }
  };

  if (!state) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center text-xs text-gray-500" data-testid="judge-workspace-loading">Loading…</div>;
  }
  if (entries.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white p-6" data-testid="judge-workspace-empty">
        <button onClick={() => navigate("/judge")} className="text-fuchsia-300 text-sm">← Inbox</button>
        <p className="mt-4 text-sm text-gray-400">No entries to judge in this contest.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="judge-workspace-page">
      <header className="flex items-center gap-2 px-3 h-12 border-b border-white/10 bg-[#0a0a10]">
        {/* Permanent exit out of the judging workflow back to the
            canonical RGM Studio Home (`/home`, which is the same
            route the top-level `/` RootRoute redirects to). Uses
            deterministic routing — not browser history — so it
            works regardless of which entry the judge is viewing or
            whether the persistent audio player is open. */}
        <button
          onClick={() => navigate("/home")}
          className="text-gray-300 hover:text-white text-sm"
          data-testid="judge-workspace-home"
          aria-label="Exit judging and go to RGM Studio Home"
        >← Home</button>
        <button onClick={() => navigate("/judge")} className="text-fuchsia-300 text-sm" data-testid="judge-workspace-back">← Inbox</button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold truncate">{state.contest.title || state.contest.slug}</p>
          <p className="text-[10px] text-gray-500">Entry {i + 1} of {entries.length} · {done} submitted</p>
        </div>
        {locked && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-200" data-testid="judge-workspace-locked-badge">Judging closed</span>}
      </header>

      <main
        className="flex-1 overflow-y-auto px-3 pt-4 max-w-2xl mx-auto w-full"
        data-testid="judge-workspace-main"
        style={{
          // Dynamic bottom clearance so the Scoring panel, Save
          // draft and Submit review controls stay tappable while
          // the persistent global player is visible at the bottom
          // of the viewport on iPhone. 56px covers the Judge
          // workspace transport bar; the +88px matches the global
          // mini-player (~64px) + its own safe-area padding and
          // only applies when the player is actually mounted.
          paddingBottom: player?.isVisible
            ? "calc(56px + 88px + env(safe-area-inset-bottom))"
            : "calc(56px + env(safe-area-inset-bottom))",
        }}
      >
        {existing?.submitted_at && (
          <p className="mb-2 text-[10px] text-emerald-300" data-testid="judge-entry-submitted-badge">✓ Submitted {new Date(existing.submitted_at).toLocaleString()}</p>
        )}
        <div className="rounded border border-white/10 bg-white/[0.02] p-3">
          <p className="text-[10px] uppercase tracking-widest text-gray-500">Entry #{i + 1}</p>
          {/* Part A — canonical track cover inside the Entry card.
              Reuses the same payload-level cover that already feeds
              the persistent player (via _buildPlayerPayload →
              getCoverUrl). Never renders an artist avatar; falls back
              to the branded RGM cover-unavailable placeholder when
              the canonical source has no usable track artwork. */}
          <div className="mt-2 w-full max-w-[220px] aspect-square rounded-lg overflow-hidden bg-black/40 border border-white/5">
            <img
              src={optimizeImage(payload?.cover_url || getCoverUrl(null), 'card')}
              alt={entry.ref_snapshot?.title || "Entry cover"}
              className="w-full h-full object-cover"
              data-testid="judge-entry-cover"
            />
          </div>
          <p className="text-sm font-bold text-white mt-3" data-testid="judge-entry-title">
            {entry.ref_snapshot?.title || entry.title || entry.entry_id.slice(0, 8)}
          </p>
          {payload ? (
            <button
              type="button"
              onClick={handlePlayToggle}
              className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs font-bold"
              data-testid="judge-entry-play-btn"
              aria-label={isThisPlaying ? "Pause entry" : "Play entry"}
            >
              <span aria-hidden="true">{isThisPlaying ? "⏸" : "▶"}</span>
              <span>{isThisPlaying ? "Pause" : "Play entry"}</span>
            </button>
          ) : (
            <p className="text-[11px] text-amber-300 mt-2" data-testid="judge-entry-no-audio">No audio available for this entry.</p>
          )}
        </div>

        <div className="mt-4">
          <ScoringPanel
            criteria={criteria} scale={scale}
            scores={draftScores} notes={draftNotes}
            disabled={locked || !!existing?.submitted_at}
            onScoreChange={(cid, v) => setDraftScores((prev) => ({ ...prev, [cid]: v }))}
            onNotesChange={setDraftNotes}
          />
        </div>
      </main>

      <nav
        className="fixed inset-x-0 z-20 border-t border-white/10 bg-[#0a0a10]"
        data-testid="judge-workspace-transport"
        style={{
          // Lift the Prev / Save / Submit / Next transport above
          // the persistent global mini-player when it is visible
          // so judges can keep audio playing while interacting with
          // the judging controls. Falls back to bottom:0 when the
          // player is closed so we don't leave a giant blank gap.
          bottom: player?.isVisible
            ? "calc(88px + env(safe-area-inset-bottom))"
            : "0",
          paddingBottom: player?.isVisible ? "0" : "env(safe-area-inset-bottom)",
        }}
      >
        <div className="max-w-2xl mx-auto flex items-center gap-2 px-3 py-2">
          <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} className="text-gray-300 hover:text-white text-sm disabled:opacity-30" data-testid="judge-prev-btn">← Prev</button>
          <button
            onClick={() => persist(false)} disabled={saving || locked || !!existing?.submitted_at}
            className="px-3 py-1.5 rounded border border-white/20 text-xs font-bold hover:bg-white/5 disabled:opacity-30"
            data-testid="judge-save-draft-btn"
          >Save draft</button>
          <button
            onClick={() => persist(true)} disabled={saving || locked || !!existing?.submitted_at}
            className="px-3 py-1.5 rounded bg-fuchsia-600 text-xs font-bold hover:bg-fuchsia-500 disabled:opacity-30 flex-1"
            data-testid="judge-submit-btn"
          >{existing?.submitted_at ? "Submitted ✓" : "Submit review"}</button>
          <button onClick={() => setI(Math.min(entries.length - 1, i + 1))} disabled={i >= entries.length - 1} className="text-gray-300 hover:text-white text-sm disabled:opacity-30" data-testid="judge-next-btn">Next →</button>
        </div>
      </nav>
    </div>
  );
}
